/* eslint-disable no-sequences */
import { Box, Button, Grid, List, ListItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import * as React from "react";
import { useState } from "react";
import { fetchData } from "../../common/services/ApiClient";
import {
  getCookiebyName,
  getExpandState,
  reduceObj,
} from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import AccordionDropdown from "../../containers/AccordionDropdown";
import LoginPanel from "../../containers/LoginPanel";
import MultiAddCerts from "../../containers/MultiAddCerts";
import MultiDropdownInput from "../../containers/MultiDropdownInput";
import ReportingPeriod from "../../containers/ReportingPeriod";
import SearchForm from "../../containers/SearchForm";
import useAppStateToFormChanges from "../../hooks/useAppStateToFormChanges";
import useMinLengthErrorValidation from "../../hooks/useMinLengthErrorValidation";
import useRangeErrorValidation from "../../hooks/useRangeErrorValidation";
import { parse, stringify } from "flatted";
import {
  Form,
  FormSelectChoice,
  KeyStringValue,
  KeyValue,
  Query,
  SubOption,
} from "../../interfaces";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import {
  commonSearchChoices,
  currencyRegEx,
  customizedComparisonSelectChoices,
  date,
  defaultRegulatoryRegionType,
  defaultSearchFieldInputLookup,
  financialReportingFormSelectChoicesBHCs,
  financialReportingFormSelectChoicesBanks,
  financialsSelectChoices,
  numericalRegEx,
  regulatorySelectChoices,
  twoDecimalPlacesRegEx,
} from "../../refdata/constants";
import BankBHCsToggle from "../BankBHCsToggle";
import { DateFilterField } from "../DateFilter";
import IncludeCheckbox from "../IncludeCheckbox";
import MoreInfoButton from "../MoreInfoButton";
import SelectFilterField from "../SelectFilterField";
import TextFilterField from "../TextFilterField";
import { generateFinancialsApiUrl } from "./FinancialReportingUtilities";
import SubSelectionCheckForm from "./SubSelectionCheckForm";

const PREFIX = "FinancialReportingSearchForm";

const classes = {
  listDisplay: `${PREFIX}-listDisplay`,
  limitRangeIcon: `${PREFIX}-limitRangeIcon`,
  rightAlignInput: `${PREFIX}-rightAlignInput`,
  endIcon: `${PREFIX}-endIcon`,
  button: `${PREFIX}-button`,
  moreInfo: `${PREFIX}-moreInfo`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.listDisplay}`]: {
    "& li": {
      display: "block",
    },
  },

  [`& .${classes.limitRangeIcon}`]: {
    fontSize: "1.5rem",
  },

  [`& .${classes.rightAlignInput}`]: {
    "& #text-filter-minimumRange-percentage": {
      textAlign: "center",
    },
    "& #text-filter-maximumRange-percentage": {
      textAlign: "center",
    },
    "& input": {
      textAlign: "right",
    },
    "& .MuiInputAdornment-positionEnd": {
      marginLeft: 0,
    },
  },

  [`& .${classes.endIcon}`]: {
    fontSize: "1rem",
    fontWeight: 400,
    fontFamily:
      "'Source Sans Pro','Helvetica Neue',Helvetica,Roboto,Arial,sans-serif",
  },

  [`& .${classes.button}`]: {
    "width": "100%",
    "height": 40,
    "marginTop": ".5rem",
    "marginBottom": ".5rem",
    "&.focus": {
      backgroundColor: "#ffffff",
      border: "1px solid rgba(172, 200, 223, 1)",
      color: theme.palette.primary.main,
    },
    "&:hover": {
      "backgroundColor": theme.palette.primary.main,
      "border": "1px solid #ced6e3",
      "color": "#ffffff",
      "&:active": {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
      },
    },
  },

  [`& .${classes.moreInfo}`]: {
    marginLeft: 60,
  },
}));

export interface FinancialReportingForm extends Form {
  name: string;
  cert: string;
  bhcName: string;
  bhcRSSD: string;
  reportPeriod: string[];
  financials: string;
  minimumRange: number | string;
  maximumRange: number | string;
  establishedStartRange: string;
  establishedEndRange: string;
  inactiveStartRange: string;
  inactiveEndRange: string;
  limitEstablishedDate: boolean;
  limitInactiveDate: boolean;
  city: string;
  bhcCity: string;
  county: string;
  bhcCounty: string;
  zip: string;
  stalp: string;
  bhcState: string;
  charterType: string;
  assetConcentration: string;
  commonSearch: string;
  charterClass: string;
  charteringAgency: string;
  primaryRegulator: string;
  profile: string;
  geographic: string;
  supervisory: string;
  fieldOffices: string;
  institutionType: string;
  multiAddCert: boolean;
  multiCert: string[];
  savedPGCSearch: boolean;
  savedGroupId: string;
  subOptionsSelected: KeyValue;
}
export default function FinancialReportingSearchForm({
  appState,
  includeMultiCert,
  initialSubmit,
  currentApp,
  noHeader,
  noNameCert,
  pgcApp,
  updateUi,
  resetForm,
  displayReset,
}: {
  appState: Query;
  includeMultiCert?: boolean;
  initialSubmit?: boolean;
  currentApp?: string;
  noHeader?: boolean;
  noNameCert?: boolean;
  pgcApp?: string;
  updateUi?: (
    uiField: string,
    uiValue: KeyValue[] | string[] | boolean | string,
  ) => void;
  resetForm?: () => void;
  displayReset?: boolean;
}) {
  const [minMaxFocus, setMinMaxFocus] = useState(false);

  const isPeerGroup = currentApp === "peerGroup";
  const dynamicCriteriaSearch = pgcApp === "Customized Comparisons";
  const [financialReportingForm, setFinancialReportingForm] =
    useState<FinancialReportingForm>({
      name: appState.search.name,
      cert: appState.filters.cert,
      bhcName: appState.search.bhcName,
      bhcRSSD: appState.filters.bhcRSSD,
      reportPeriod: appState.filters.reportPeriod,
      financials: appState.filters.financials,
      minimumRange: appState.filters.minimumRange,
      maximumRange: appState.filters.maximumRange,
      establishedStartRange: appState.filters.establishedStartRange,
      establishedEndRange: appState.filters.establishedEndRange,
      inactiveStartRange: appState.filters.inactiveStartRange,
      inactiveEndRange: appState.filters.inactiveEndRange,
      limitEstablishedDate: appState.ui.limitEstablishedDate,
      limitInactiveDate: appState.ui.limitInactiveDate,
      city: appState.search.city,
      bhcCity: appState.search.bhcCity,
      county: appState.filters.county,
      bhcCounty: appState.filters.bhcCounty,
      stalp: appState.filters.stalp,
      bhcState: appState.filters.bhcState,
      zip: appState.filters.zip,
      charterType: appState.filters.charterType,
      assetConcentration: appState.filters.assetConcentration,
      commonSearch: appState.filters.commonSearch,
      charterClass: appState.filters.charterClass,
      charteringAgency: appState.filters.charteringAgency,
      primaryRegulator: appState.filters.primaryRegulator,
      profile: appState.filters.profile,
      geographic: appState.filters.geographic,
      supervisory: appState.filters.supervisory,
      fieldOffices: appState.filters.fieldOffices,
      institutionType: appState.ui.institutionType,
      msa: appState.filters.msa,
      multiAddCert: includeMultiCert ? true : false,
      multiCert: appState.filters.multiCert ? appState.filters.multiCert : [],
      savedPGCSearch: appState.ui.savedPGCSearch,
      savedGroupId: appState.ui.savedGroupId,
      subOptionsSelected: appState.ui.subOptionsSelected,
    });
  const [previousFinancials, setPreviousFinancials] = React.useState(
    appState.filters.financials,
  );
  const [counties, setCounties] = useState<{ label: string; value: string }[]>(
    [],
  );

  const financialsSearchFieldInputs = {
    ...defaultSearchFieldInputLookup,
    name: {
      ...defaultSearchFieldInputLookup.name,
      apiPath: "financials",
      uniqueField: "NAMEFULL",
    },
    bhcName: {
      ...defaultSearchFieldInputLookup.bhcName,
      apiPath: "financials",
      uniqueField: "NAMEHCR",
    },
  };
  const financialReportingErrorMetaData = {
    minimumRange: {
      min: financialReportingForm.minimumRange,
      max: financialReportingForm.maximumRange,
    },
    maximumRange: {
      min: financialReportingForm.minimumRange,
      max: financialReportingForm.maximumRange,
    },
    name: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    bhcName: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    bhcRSSD: {
      minLength: 6,
      helperText: "Must be a 6 - 7 Digit Number",
    },
    city: {
      minLength: 3,
      helperText: "Minimum Length of 3 Characters",
    },
    bhcCity: {
      minLength: 3,
      helperText: "Minimum Length of 3 Characters",
    },
  };

  const [rangeErrors, setRangeErrors] = useState<Set<string>>(new Set());
  const [minLengthErrors, setMinLengthErrors] = useState<Set<string>>(
    new Set(),
  );
  const dateErrors: KeyValue = React.useMemo(() => {
    return {};
  }, []);

  const [regulatoryOptions, setRegulatoryOptions] = React.useState<KeyValue>(
    defaultRegulatoryRegionType,
  );
  const { prevForm, setPrevForm } =
    useAppStateToFormChanges<FinancialReportingForm>(
      appState.filters,
      appState.search,
      setFinancialReportingForm,
    );

  const [initialPageLoad, setInitialPageLoad] = React.useState(true);

  const [prevFormForField, setPrevFormForField] = React.useState(
    financialReportingForm,
  );

  // Sub-Peer Group options
  const [subOptions, setSubOptions] = useState<SubOption[]>([]);
  const [displaySubGroups, setDisplaySubGroups] =
    React.useState<boolean>(false);
  const [subOptionsSelected, setSubOptionsSelected] = React.useState<KeyValue>(
    appState.ui?.subOptionsSelected ?? {},
  );

  const disabled =
    minLengthErrors.size !== 0 ||
    rangeErrors.size !== 0 ||
    (Object.keys(dateErrors).length > 0 &&
      financialReportingForm.limitEstablishedDate) ||
    (Object.keys(dateErrors).length > 0 &&
      financialReportingForm.limitInactiveDate) ||
    (!financialReportingForm.savedGroupId &&
      financialReportingForm.savedPGCSearch) ||
    (displaySubGroups &&
      Object.values(subOptionsSelected).filter((value) => value === true)
        .length === 0);

  const reportingYear = financialReportingForm.reportPeriod?.[0]?.substring(
    0,
    4,
  );
  const reportingQuarter = financialReportingForm.reportPeriod?.[0]?.substring(
    4,
    8,
  );
  const currency = !financialReportingForm.financials?.includes("ROAPTX");
  const percentage = financialReportingForm.financials?.includes("ROAPTX");
  const startIconSymbol = currency ? (
    <Typography color="primary.main" className={classes.limitRangeIcon}>
      $
    </Typography>
  ) : (
    <></>
  );
  const percentEndIcon = (
    <Typography
      style={{
        fontSize: 24,
      }}
      color="primary.main"
      className={classes.endIcon}
    >
      %
    </Typography>
  );
  useMinLengthErrorValidation(
    financialReportingForm,
    financialReportingErrorMetaData,
    setMinLengthErrors,
  );

  useRangeErrorValidation(
    financialReportingForm,
    financialReportingErrorMetaData,
    setRangeErrors,
  );

  const getRegionType = (regionType: KeyValue) => {
    if (regionType.profile) return "profile";
    if (regionType.geographic) return "geographic";
    if (regionType.supervisory) return "supervisory";
    if (regionType.fieldOffices) return "fieldOffices";
    if (regionType.msa) return "msa";
    return "";
  };
  const [msas, setMsas] = React.useState<FormSelectChoice[]>([]);

  React.useEffect(() => {
    let isMounted = true;

    const params = new URLSearchParams({
      filters: `MSABR:>0 AND !MSANAMB:""`,
      fields: `MSABR,MSANAMB`,
      limit: `1000`,
      get_unique: `MSABR`,
      sort_by: `MSANAMB`,
      sort_order: `ASC`,
    });

    fetchData(`${ApiBaseUrl()}/sod?${params.toString()}`)
      .then((response) => {
        const msaOptions: FormSelectChoice[] = response.data.map(
          (item: any) => {
            const row = item.data ?? item;
            return {
              label: String(row.MSANAMB ?? "").trim(),
              value: String(row.MSABR ?? "").trim(),
            };
          },
        );
        if (isMounted) {
          setMsas([{ label: "Select MSA", value: "" }, ...msaOptions]);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const [savedPeerGroups, setSavedPeerGroups] = React.useState<
    FormSelectChoice[]
  >([]);

  const [user, setUser] = useState<string | undefined>(
    getCookiebyName("uuid") || undefined,
  );

  //this uses the stored form data to load the form where the user left off, e.g. 'saved search by geo region'
  React.useEffect(() => {
    const newUser = getCookiebyName("uuid");
    if (!user && newUser) {
      const storedData = localStorage.getItem("appStateUi");
      if (storedData) {
        const data = parse(storedData);
        updateUi &&
          updateUi("standardGroupSearchPills", data?.standardGroupSearchPills);
        updateUi && updateUi("individualCert", data?.individualCert);
        updateUi && updateUi("savedGroupId", data?.savedGroupId);
        updateUi && updateUi("savedPGCSearch", data?.savedPGCSearch);
        setFinancialReportingForm({
          ...financialReportingForm,
          savedGroupId: data?.savedGroupId,
          savedPGCSearch: data?.savedPGCSearch,
        });
      } else if (appState.ui?.savedGroupId) {
        setFinancialReportingForm({
          ...financialReportingForm,
          savedGroupId: appState.ui?.savedGroupId,
          savedPGCSearch: appState.ui?.savedPGCSearch,
        });
      }
    }

    setUser(newUser);
  }, [
    appState.ui?.savedGroupId,
    appState.ui?.savedPGCSearch,
    financialReportingForm,
    updateUi,
    user,
  ]);

  //this stores the last search
  React.useEffect(() => {
    if (
      financialReportingForm.savedGroupId ||
      financialReportingForm.savedGroupId === ""
    ) {
      // CodeQL: js/clear-text-storage-of-sensitive-data: false positive
      const locallyStoredAppStateUi = localStorage.getItem("appStateUi");
      const appStateUi = JSON.parse(JSON.stringify(appState.ui));
      const storedAppStateUi = locallyStoredAppStateUi
        ? parse(locallyStoredAppStateUi)
        : appStateUi;
      storedAppStateUi.savedGroupId = financialReportingForm.savedGroupId;
      storedAppStateUi.savedPGCSearch = financialReportingForm.savedPGCSearch;
      // CodeQL: js/clear-text-storage-of-sensitive-data: false positive
      localStorage.setItem("appStateUi", stringify(storedAppStateUi));
    }
  }, [
    appState.ui,
    financialReportingForm.savedGroupId,
    financialReportingForm.savedPGCSearch,
  ]);

  React.useEffect(() => {
    if (
      !financialReportingForm.savedPGCSearch &&
      financialReportingForm.savedGroupId
    ) {
      setFinancialReportingForm({
        ...financialReportingForm,
        savedGroupId: "",
      });
    }
  }, [financialReportingForm]);

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && user && financialReportingForm.savedPGCSearch) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups?filters=is_custom:true&user=${user}&limit=10000&offset=0`,
        )
          .then((response) => {
            setSavedPeerGroups(
              response.data.map((e: KeyValue) => ({
                label: e.name,
                value: e.ID,
              })),
            );
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [financialReportingForm.savedPGCSearch, user]);

  React.useEffect(() => {
    if (
      JSON.stringify({ ...prevFormForField, fieldOffices: "" }) !==
        JSON.stringify({ ...financialReportingForm, fieldOffices: "" }) &&
      financialReportingForm.reportPeriod?.[0]
    ) {
      let isMounted = true;
      setPrevFormForField({ ...financialReportingForm, fieldOffices: "" });
      (async () => {
        await fetchData(
          `${generateFinancialsApiUrl(
            financialReportingForm,
            ["FLDOFF"],
            "fieldOffices",
          )}&sort_by=FLDOFF&sort_order=asc`,
        )
          .then((response) => {
            if (isMounted && response.data) {
              const responseData = response.data.map(
                (office: KeyStringValue) => {
                  return { value: office.FLDOFF, label: office.FLDOFF };
                },
              );
              setRegulatoryOptions((prev) => ({
                ...prev,
                fieldOffices: [
                  { value: "", label: "Select FDIC Field Offices" },
                  { value: "1", label: "All Field Offices" },
                  ...responseData,
                ],
              }));
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [financialReportingForm, prevFormForField, regulatoryOptions]);

  React.useEffect(() => {
    if (
      financialReportingForm.financials !== previousFinancials &&
      initialPageLoad
    ) {
      setInitialPageLoad(false);
      setPreviousFinancials(financialReportingForm.financials);
      setFinancialReportingForm({
        ...financialReportingForm,
        minimumRange: "",
        maximumRange: "",
      });
    }
  }, [
    appState.filters.fieldOffices,
    financialReportingForm,
    initialPageLoad,
    previousFinancials,
  ]);

  const endDateMin = (startRange: string, endRange: string) =>
    new Date(endRange).getTime() > new Date(date.financialMin).getTime()
      ? startRange
      : date.financialMin;

  const dateRange = (type: "Established" | "Inactive") => {
    const lowerCaseType = type.toLocaleLowerCase();
    return (
      <>
        <Box
          id={`limit${type}DateRange`}
          sx={{ margin: "-.75rem 0 .75rem .75rem", display: "flex" }}
        >
          <IncludeCheckbox
            currentValue={financialReportingForm[`limit${type}Date`] as boolean}
            form={financialReportingForm}
            setForm={setFinancialReportingForm}
            label="Limit Date Range"
            fieldName={`limit${type}Date`}
          />
          <MoreInfoButton
            id={`limit${type}DateRangeInfo`}
            popperPosition="right"
            label={`Limit ${type} Date Range`}
            hover
          >
            {type === "Established" ? (
              <Typography>
                Limiting the date range will reveal new institutions opened
                within that time frame.
              </Typography>
            ) : (
              <>
                <Typography>
                  Limiting the date range will reveal institutions that became
                  inactive within that time frame.
                </Typography>
                <Typography>
                  <span style={{ fontWeight: "bold" }}>Note: </span>
                  This section is independent from "Reporting Period", which
                  will be deactivated.
                </Typography>
              </>
            )}
          </MoreInfoButton>
        </Box>
        {type === "Inactive" && financialReportingForm.limitInactiveDate && (
          <Typography
            style={{
              fontStyle: "italic",
              color: "#017B7B",
              textAlign: "center",
              fontSize: "1rem",
              marginBottom: "1.5rem",
            }}
            id="reporting-period-deactivated"
          >
            Reporting Period is deactivated
          </Typography>
        )}
        <DateFilterField
          disabled={!financialReportingForm[`limit${type}Date`]}
          currentValue={
            financialReportingForm[`${lowerCaseType}StartRange`] as string
          }
          fieldName={`${lowerCaseType}StartRange`}
          id={`${lowerCaseType}-start-range`}
          label="Start Range"
          minDateValue={
            type === "Inactive" ? date.inactiveMin : date.financialMin
          }
          maxDateValue={
            financialReportingForm[`${lowerCaseType}EndRange`] as string
          }
          errors={dateErrors}
          form={financialReportingForm}
          setForm={setFinancialReportingForm}
        />
        <DateFilterField
          disabled={!financialReportingForm[`limit${type}Date`]}
          currentValue={
            financialReportingForm[`${lowerCaseType}EndRange`] as string
          }
          fieldName={`${lowerCaseType}EndRange`}
          id={`${lowerCaseType}-end-range`}
          label="End Range"
          minDateValue={endDateMin(
            financialReportingForm[`${lowerCaseType}StartRange`] as string,
            financialReportingForm[`${lowerCaseType}EndRange`] as string,
          )}
          maxDateValue={new Date().toLocaleDateString()}
          errors={dateErrors}
          form={financialReportingForm}
          setForm={setFinancialReportingForm}
        />
      </>
    );
  };

  const reportPeriodSearch = (
    <ListItem>
      <AccordionDropdown
        id="reportingPeriod"
        title="Reporting Period"
        defaultExpand={getExpandState(appState.ui.reportingPeriodExpand, true)}
        body={
          financialReportingForm.limitInactiveDate &&
          financialReportingForm.institutionType === "banks" ? (
            <Typography
              style={{
                fontStyle: "italic",
                color: "#017B7B",
                fontSize: "1rem",
                textAlign: "center",
                marginBottom: "1rem",
              }}
              id="inactive-date-range-in-use"
            >
              Inactive Date Range is in use
            </Typography>
          ) : (
            <ReportingPeriod
              currentQuarterValue={reportingQuarter}
              currentYearValue={reportingYear}
              setForm={setFinancialReportingForm}
              form={financialReportingForm}
            />
          )
        }
      />
    </ListItem>
  );
  const certSearchType = includeMultiCert && !dynamicCriteriaSearch && (
    <ListItem>
      <AccordionDropdown
        id="institutionType"
        title="Cert Search Type"
        defaultExpand={getExpandState(appState.ui.institutionTypeExpand, true)}
        body={
          <>
            <Grid container>
              <Grid size={6}>
                <Button
                  id="manual-add-cert-button"
                  onClick={() =>
                    setFinancialReportingForm({
                      ...financialReportingForm,
                      multiAddCert: true,
                    })
                  }
                  color="primary"
                  className={clsx(
                    classes.button,
                    financialReportingForm.multiAddCert && "focus",
                  )}
                >
                  Add Certs
                </Button>
              </Grid>
              <Grid size={6}>
                <Button
                  id={"search-institutions-button"}
                  onClick={() =>
                    setFinancialReportingForm({
                      ...financialReportingForm,
                      multiAddCert: false,
                    })
                  }
                  color="primary"
                  className={clsx(
                    classes.button,
                    !financialReportingForm.multiAddCert && "focus",
                  )}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </>
        }
      />
    </ListItem>
  );

  const bankValues = [
    "name",
    "cert",
    "financials",
    "minimumRange",
    "maximumRange",
    "limitEstablishedDate",
    "limitInactiveDate",
    "zip",
    "charterType",
    "assetConcentration",
    "commonSearch",
    "charterClass",
    "charteringAgency",
    "primaryRegulator",
    "profile",
    "geographic",
    "supervisory",
    "fieldOffices",
  ];

  const bhcsValues = ["bhcName", "bhcRSSD"];

  const selectPeerGroupOptions = [
    { label: "All Institutions", value: "allInst" },
    { label: "Commercial Institutions", value: "allCommercial" },
    { label: "Savings Institutions", value: "allSavings" },
    { label: "Federal Charters", value: "fedCharters" },
    { label: "State Charters", value: "stateCharters" },
    { label: "By Asset Size", value: "assetSize" },
    { label: "By Asset Concentration", value: "assetConcentration" },
    { label: "By Geographic Region", value: "geographicRegion" },
    { label: "---", value: "---" },
    { label: "Saved Custom Peer Groups", value: "savedPeerGroups" },
  ];

  React.useEffect(() => {
    const assetSizeSubOptions = [
      "Less than $100M",
      "$100M - $1B",
      "$1B - $10B",
      "$10B - $250B",
      "Greater than $250B",
    ];
    const assetConcentrationSubOptions = [
      "Credit Card Banks",
      "International Banks",
      "Agricultural Banks",
      "Commercial Lenders",
      "Mortgage Lenders",
      "Consumer Lenders",
      "All Other Specialized < $1B",
      "All Other < $1B",
      "All Other > $1B",
    ];
    const geoRegionSubOptions = [
      "Atlanta",
      "Chicago",
      "Dallas",
      "Kansas City",
      "New York",
      "San Francisco",
    ];
    const subOptionLookup: KeyValue = {
      assetSize: assetSizeSubOptions,
      assetConcentration: assetConcentrationSubOptions,
      geographicRegion: geoRegionSubOptions,
      savedPeerGroups: savedPeerGroups,
    };

    setDisplaySubGroups(
      financialReportingForm.savedGroupId === "assetSize" ||
        financialReportingForm.savedGroupId === "assetConcentration" ||
        financialReportingForm.savedGroupId === "geographicRegion" ||
        financialReportingForm.savedGroupId === "savedPeerGroups" ||
        false,
    );
    setSubOptions(subOptionLookup[financialReportingForm.savedGroupId] || []);
    if (
      !subOptionLookup[financialReportingForm.savedGroupId] &&
      financialReportingForm.subOptionsSelected &&
      subOptions.length === 0 &&
      Object.keys(financialReportingForm.subOptionsSelected).length > 0
    ) {
      setFinancialReportingForm({
        ...financialReportingForm,
        subOptionsSelected: {},
      });
      setSubOptionsSelected({});
    }
  }, [financialReportingForm, savedPeerGroups, subOptions.length]);

  React.useEffect(() => {
    let stringArrayOfKeys: any = subOptions;
    if (!subOptions.every((item) => typeof item === "string")) {
      stringArrayOfKeys = subOptions.map((c) => c.value);
    }
    const allKeysExist = stringArrayOfKeys.every(
      (key: string) => key in subOptionsSelected,
    );
    if (!allKeysExist && subOptions.length > 0) {
      if (
        appState.ui?.peerGroups.length > 0 &&
        Object.keys(subOptionsSelected).length
      ) {
        const options = subOptionsSelected;
        appState.ui.peerGroups.forEach((option: string) => {
          if (options.hasOwnProperty(option)) {
            options[option] = true;
          }
        });
        setSubOptionsSelected(options);
      } else {
        const initialSelected =
          financialReportingForm.savedGroupId !== "savedPeerGroups";
        if (subOptions.every((item) => typeof item === "string")) {
          setSubOptionsSelected(
            reduceObj(
              subOptions.map((curr: any) => {
                return {
                  key: curr,
                  value: initialSelected,
                };
              }),
            ),
          );
        } else {
          setSubOptionsSelected(
            reduceObj(
              subOptions.map((curr: any) => {
                return {
                  key: curr.value,
                  value: initialSelected,
                };
              }),
            ),
          );
        }
      }
    }
  }, [
    appState.ui.peerGroups,
    financialReportingForm.savedGroupId,
    subOptions,
    subOptionsSelected,
  ]);
  React.useEffect(() => {
    if (
      subOptionsSelected &&
      Object.keys(subOptionsSelected).length > 0 &&
      JSON.stringify(subOptionsSelected) !==
        JSON.stringify(financialReportingForm.subOptionsSelected)
    ) {
      setFinancialReportingForm({
        ...financialReportingForm,
        subOptionsSelected: subOptionsSelected,
      });
    }
  }, [financialReportingForm, subOptionsSelected]);
  const handleSubCheckboxChange = (e: any) => {
    const id = e.target.id;
    setSubOptionsSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const subSelectionCaption = () => {
    const numSelected = Object.values(subOptionsSelected).filter(
      (value) => value === true,
    ).length;
    if (numSelected === 0) {
      return "Nothing Selected";
    } else if (numSelected === subOptions.length) {
      return "All Selected";
    } else {
      return "Custom Selection";
    }
  };

  const notSignedInWhileViewingSavedPG =
    financialReportingForm.savedGroupId === "savedPeerGroups" &&
    subOptions.length === 0 &&
    !getCookiebyName("uuid");

  const subSelectionIndeterminate = () => {
    const numSelected = Object.values(subOptionsSelected).filter(
      (value) => value === true,
    ).length;
    return subOptions.length !== numSelected && numSelected !== 0;
  };

  const handleStateChange = async (
    stalp: string,
    initialPageLoad: boolean = false,
  ) => {
    !initialPageLoad &&
      setFinancialReportingForm((prev) => ({ ...prev, county: "" }));
    if (!stalp) return;
    await fetchData(
      `${ApiBaseUrl()}/locations?filters=STALP:"${stalp}"&agg_by=COUNTY&agg_limit=10000`,
    )
      .then((response) => {
        const formattedCounties = response.data
          .map((e: any) => ({
            label: e.COUNTY,
            value: e.COUNTY,
          }))
          .filter((county: any) => county.label && county.value);
        setCounties(formattedCounties);
      })
      .catch((error: Error) => {
        console.error(error);
      });
  };

  // This loads the counties and retains the current county selection when the page loads WITH county selected. ie. peergroups
  React.useEffect(() => {
    if (
      (initialPageLoad &&
        (financialReportingForm.county || financialReportingForm.bhcCounty)) ||
      (financialReportingForm.stalp && counties.length === 0)
    ) {
      handleStateChange(
        financialReportingForm.stalp || financialReportingForm.bhcState,
        initialPageLoad,
      );
    }
  }, [
    counties.length,
    financialReportingForm.bhcCounty,
    financialReportingForm.bhcState,
    financialReportingForm.county,
    financialReportingForm.stalp,
    initialPageLoad,
  ]);

  React.useEffect(() => {
    if (!financialReportingForm.stalp) {
      setFinancialReportingForm((prev) => ({ ...prev, county: "" }));
    }
  }, [financialReportingForm.stalp]);

  const FinancialReportingList =
    financialReportingForm.multiAddCert && !dynamicCriteriaSearch ? (
      <List className={classes.listDisplay}>
        {certSearchType}
        <ListItem id="multi-cert-li">
          <Typography
            variant="h4"
            color="primary.main"
            style={{ marginTop: 16 }}
          >
            Add Certs Manually
          </Typography>
          <MultiAddCerts />
        </ListItem>
      </List>
    ) : (
      <List className={classes.listDisplay}>
        {certSearchType}
        <ListItem>
          {!isPeerGroup && (
            <AccordionDropdown
              id="institutionType"
              title="Institution Type"
              defaultExpand={getExpandState(
                appState.ui.institutionTypeExpand,
                true,
              )}
              body={
                <BankBHCsToggle
                  form={financialReportingForm}
                  setForm={setFinancialReportingForm}
                  bankValues={bankValues}
                  bhcValues={bhcsValues}
                  displayInfo
                />
              }
            />
          )}
          {!appState.ui.createGroupType &&
            !window.location.pathname.includes("financialreporting") && (
              <AccordionDropdown
                id="reportPopulation"
                title="Report Population"
                defaultExpand={getExpandState(
                  appState.ui.institutionTypeExpand,
                  true,
                )}
                body={
                  <>
                    <LoginPanel noDisplay />
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Button
                          id="pgcSearch-button"
                          onClick={() => {
                            setFinancialReportingForm({
                              ...financialReportingForm,
                              savedPGCSearch: false,
                            });
                            updateUi && updateUi("savedPGCSearch", false);
                          }}
                          color="primary"
                          className={clsx(
                            classes.button,
                            !financialReportingForm.savedPGCSearch && "focus",
                          )}
                        >
                          Search
                        </Button>
                      </Grid>
                      <Grid size={6}>
                        <Button
                          id={"report-population-button"}
                          onClick={() => {
                            setFinancialReportingForm({
                              ...financialReportingForm,
                              savedPGCSearch: true,
                            });
                            updateUi && updateUi("savedPGCSearch", true);
                          }}
                          color="primary"
                          className={clsx(
                            classes.button,
                            financialReportingForm.savedPGCSearch && "focus",
                          )}
                        >
                          Peer Groups
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                }
              />
            )}
        </ListItem>

        {financialReportingForm.savedPGCSearch ? (
          <>
            {
              <>
                <ListItem style={{ paddingTop: 20 }}>
                  <SelectFilterField
                    onChange={(e: any) =>
                      setFinancialReportingForm({
                        ...financialReportingForm,
                        savedGroupId: e.target.value,
                      })
                    }
                    currentValue={
                      financialReportingForm.savedGroupId
                        ? financialReportingForm.savedGroupId
                        : ""
                    }
                    label="Peer Group"
                    fieldName="savedPeerGroups"
                    options={selectPeerGroupOptions}
                    disabledOptions={["---"]}
                    setForm={setFinancialReportingForm}
                    form={financialReportingForm}
                  />
                </ListItem>
                {notSignedInWhileViewingSavedPG ? (
                  <ListItem>
                    <Typography
                      variant="body2"
                      style={{ paddingTop: 16, textAlign: "center" }}
                    >
                      You must be signed-in to view your saved content.
                    </Typography>
                  </ListItem>
                ) : (
                  displaySubGroups && (
                    <ListItem>
                      <SubSelectionCheckForm
                        type="Sub-Peer Groups"
                        options={subOptions}
                        onChange={handleSubCheckboxChange}
                        checkedStatus={
                          financialReportingForm.subOptionsSelected
                        }
                        setCheckedStatus={setSubOptionsSelected}
                        subCaption={subSelectionCaption()}
                        indeterminate={subSelectionIndeterminate()}
                      />
                    </ListItem>
                  )
                )}
              </>
            }
          </>
        ) : (
          <>
            {!noNameCert && (
              <ListItem id="name-cert-bhc-id-li">
                <AccordionDropdown
                  id="institution"
                  title={
                    !isPeerGroup
                      ? financialReportingForm.institutionType === "banks"
                        ? "Bank Name/Cert"
                        : "Bank Holding Co. Name/ID"
                      : "Name / Cert"
                  }
                  defaultExpand={getExpandState(
                    appState.ui.institutionExpand,
                    false,
                  )}
                  body={
                    <>
                      {(isPeerGroup ||
                        financialReportingForm.institutionType === "banks") && (
                        <MultiDropdownInput
                          selectId="name-cert"
                          form={financialReportingForm}
                          setForm={setFinancialReportingForm}
                          initialSearchFieldName={
                            appState.search.name ? "name" : "cert"
                          }
                          formSelectChoices={
                            !isPeerGroup
                              ? financialReportingFormSelectChoicesBanks
                              : customizedComparisonSelectChoices
                          }
                          searchFieldInputLookup={financialsSearchFieldInputs}
                          errors={minLengthErrors}
                          formMetaData={financialReportingErrorMetaData}
                          pgcApp={pgcApp}
                        />
                      )}
                      {financialReportingForm.institutionType === "bhcs" && (
                        <MultiDropdownInput
                          selectId="bhcNameId"
                          form={financialReportingForm}
                          setForm={setFinancialReportingForm}
                          initialSearchFieldName={
                            appState.search.bhcName
                              ? "bhcName"
                              : appState.filters.bhcRSSD
                                ? "bhcRSSD"
                                : ""
                          }
                          formSelectChoices={
                            financialReportingFormSelectChoicesBHCs
                          }
                          searchFieldInputLookup={financialsSearchFieldInputs}
                          errors={minLengthErrors}
                          formMetaData={financialReportingErrorMetaData}
                        />
                      )}
                    </>
                  }
                />
              </ListItem>
            )}

            {financialReportingForm.institutionType === "banks" && (
              <>
                <ListItem>
                  <AccordionDropdown
                    id="establishedDateRange"
                    title="Established Date Range"
                    defaultExpand={getExpandState(
                      appState.ui.establishedDateRangeExpand,
                      false,
                    )}
                    body={dateRange("Established")}
                  />
                </ListItem>
                <ListItem>
                  <AccordionDropdown
                    id="inactiveDateRange"
                    title="Inactive Date Range"
                    defaultExpand={getExpandState(
                      appState.ui.inactiveDateRangeExpand,
                      false,
                    )}
                    body={dateRange("Inactive")}
                  />
                </ListItem>
              </>
            )}
            {reportPeriodSearch}
            <ListItem>
              {financialReportingForm.institutionType === "banks" ||
              isPeerGroup ? (
                <AccordionDropdown
                  id="locations"
                  title="Locations"
                  defaultExpand={getExpandState(
                    appState.ui.locationsExpand,
                    false,
                  )}
                  body={
                    <>
                      <List>
                        <ListItem>
                          <SelectFilterField
                            currentValue={financialReportingForm.stalp}
                            fieldName={"stalp"}
                            label="State/Territory"
                            options={selectedStatesAlpha}
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            onChange={(e) => handleStateChange(e.target.value)}
                          />
                        </ListItem>
                        <ListItem>
                          <SelectFilterField
                            currentValue={financialReportingForm.county}
                            fieldName={"county"}
                            label="County"
                            options={counties}
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            disabled={
                              counties.length === 0 ||
                              !financialReportingForm.stalp
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <TextFilterField
                            autoSuggest
                            currentValue={
                              financialReportingForm.city
                                ? financialReportingForm.city
                                : ""
                            }
                            fieldName={"city"}
                            label="City"
                            apiPath="institutions"
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            error={minLengthErrors.has("city")}
                            helperText={
                              minLengthErrors.has("city")
                                ? financialReportingErrorMetaData.city
                                    .helperText
                                : ""
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <TextFilterField
                            currentValue={financialReportingForm.zip}
                            apiPath="institutions"
                            fieldName="zip"
                            label="Zip Code"
                            regEx={numericalRegEx}
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                          />
                        </ListItem>
                      </List>
                    </>
                  }
                />
              ) : (
                <AccordionDropdown
                  id="locations"
                  title="Locations"
                  defaultExpand={getExpandState(
                    appState.ui.locationsExpand,
                    false,
                  )}
                  body={
                    <>
                      <List>
                        <ListItem>
                          <SelectFilterField
                            currentValue={financialReportingForm.stalp}
                            fieldName={"stalp"}
                            label="State/Territory"
                            options={selectedStatesAlpha}
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            onChange={(e) => handleStateChange(e.target.value)}
                          />
                        </ListItem>
                        <ListItem>
                          <SelectFilterField
                            currentValue={financialReportingForm.county}
                            fieldName={"county"}
                            label="County"
                            options={counties}
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            disabled={
                              counties.length === 0 ||
                              !financialReportingForm.stalp
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <TextFilterField
                            autoSuggest
                            currentValue={
                              financialReportingForm.city
                                ? financialReportingForm.city
                                : ""
                            }
                            uniqueField="CITYHCR"
                            fieldName={"city"}
                            label="City"
                            apiPath="institutions"
                            form={financialReportingForm}
                            setForm={setFinancialReportingForm}
                            error={minLengthErrors.has("city")}
                            helperText={
                              minLengthErrors.has("city")
                                ? financialReportingErrorMetaData.city
                                    .helperText
                                : ""
                            }
                          />
                        </ListItem>
                      </List>
                    </>
                  }
                />
              )}
            </ListItem>
            {(isPeerGroup ||
              financialReportingForm.institutionType === "banks") && (
              <>
                <ListItem>
                  <AccordionDropdown
                    id="financials"
                    title="Financials"
                    defaultExpand={getExpandState(
                      appState.ui.financialsExpand,
                      true,
                    )}
                    body={
                      <>
                        <SelectFilterField
                          currentValue={financialReportingForm.financials}
                          label="Search Type"
                          fieldName="financials"
                          options={financialsSelectChoices}
                          setForm={setFinancialReportingForm}
                          form={financialReportingForm}
                        />
                        {financialReportingForm.financials !== "" ? (
                          <>
                            <Typography
                              variant="h3"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Limit or Range
                              <Typography
                                style={{ fontSize: 14, color: "#1A4480" }}
                                sx={{
                                  display: "inline",
                                }}
                              >
                                {" "}
                                - {currency ? ` Dollars` : ` Percent`}
                              </Typography>
                              <MoreInfoButton
                                id="limitOrRange"
                                popperPosition="right"
                                label="Limit or Range - Dollars"
                                hover
                              >
                                <Typography>
                                  These fields are optional. Use these fields
                                  individually or combined to find institutions
                                  above a specified minimum, all matching up to
                                  a specified maximum, or those within a
                                  specified range.
                                </Typography>
                              </MoreInfoButton>
                            </Typography>
                            <TextFilterField
                              showLabel
                              inputFocus={setMinMaxFocus}
                              startIcon={startIconSymbol}
                              endIcon={percentage ? percentEndIcon : undefined}
                              additionalStyles={classes.rightAlignInput}
                              currentValue={financialReportingForm.minimumRange}
                              currency={currency}
                              percentage={percentage}
                              label="Minimum"
                              fieldName="minimumRange"
                              form={financialReportingForm}
                              setForm={setFinancialReportingForm}
                              regEx={
                                percentage
                                  ? twoDecimalPlacesRegEx
                                  : currencyRegEx
                              }
                              error={
                                rangeErrors.has("maximumRange") && !minMaxFocus
                              }
                              helperText={
                                rangeErrors.has("maximumRange") && !minMaxFocus
                                  ? "May not be greater than “Maximum” Field"
                                  : ""
                              }
                            />
                            <TextFilterField
                              showLabel
                              inputFocus={setMinMaxFocus}
                              startIcon={startIconSymbol}
                              endIcon={percentage ? percentEndIcon : undefined}
                              additionalStyles={classes.rightAlignInput}
                              currentValue={financialReportingForm.maximumRange}
                              currency={currency}
                              percentage={percentage}
                              label="Maximum"
                              fieldName="maximumRange"
                              form={financialReportingForm}
                              setForm={setFinancialReportingForm}
                              regEx={
                                percentage
                                  ? twoDecimalPlacesRegEx
                                  : currencyRegEx
                              }
                              error={
                                rangeErrors.has("maximumRange") && !minMaxFocus
                              }
                              helperText={
                                rangeErrors.has("maximumRange") && !minMaxFocus
                                  ? "May not be less than “Minimum” Field"
                                  : ""
                              }
                            />
                          </>
                        ) : (
                          ""
                        )}
                      </>
                    }
                  />
                </ListItem>
                <ListItem>
                  <AccordionDropdown
                    id="commonSearches"
                    title="Common Searches"
                    defaultExpand={getExpandState(
                      appState.ui.commonSearchesExpand,
                      false,
                    )}
                    body={
                      <>
                        <List>
                          <ListItem>
                            <SelectFilterField
                              currentValue={financialReportingForm.charterType}
                              label="Charter Type"
                              fieldName="charterType"
                              options={commonSearchChoices.charterType}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                          <ListItem>
                            <SelectFilterField
                              currentValue={
                                financialReportingForm.assetConcentration
                              }
                              label="Asset Concentration"
                              fieldName="assetConcentration"
                              options={commonSearchChoices.assetConcentration}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                          <ListItem>
                            <SelectFilterField
                              currentValue={financialReportingForm.commonSearch}
                              label="Common Search"
                              fieldName="commonSearch"
                              options={commonSearchChoices.commonSearch}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                        </List>
                      </>
                    }
                  />
                </ListItem>
                <ListItem>
                  <AccordionDropdown
                    id="regulatory"
                    title="Regulatory"
                    defaultExpand={getExpandState(
                      appState.ui.regulatoryExpand,
                      false,
                    )}
                    body={
                      <>
                        <List>
                          <ListItem>
                            <SelectFilterField
                              currentValue={financialReportingForm.charterClass}
                              label="Bank Chartering Class"
                              fieldName="charterClass"
                              options={regulatorySelectChoices.charterClass}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                          <ListItem>
                            <SelectFilterField
                              currentValue={
                                financialReportingForm.charteringAgency
                              }
                              label="Chartering Agency"
                              fieldName="charteringAgency"
                              options={regulatorySelectChoices.charteringAgency}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                          <ListItem>
                            <SelectFilterField
                              currentValue={
                                financialReportingForm.primaryRegulator
                              }
                              label="Primary Federal Regulator"
                              fieldName="primaryRegulator"
                              options={regulatorySelectChoices.primaryRegulator}
                              setForm={setFinancialReportingForm}
                              form={financialReportingForm}
                            />
                          </ListItem>
                          <ListItem>
                            <MultiDropdownInput
                              select
                              selectId="region-type-office"
                              label="FDIC Region Type/Office"
                              form={financialReportingForm}
                              setForm={setFinancialReportingForm}
                              initialSearchFieldName={getRegionType(
                                appState.filters,
                              )}
                              formSelectChoices={
                                regulatorySelectChoices.regionTypeOffice
                              }
                              searchFieldInputLookup={
                                defaultRegulatoryRegionType
                              }
                              secondarySelectOptions={{
                                ...regulatoryOptions,
                                msa: msas,
                              }}
                            />
                          </ListItem>
                        </List>
                      </>
                    }
                  />
                </ListItem>
              </>
            )}
          </>
        )}
      </List>
    );

  React.useEffect(() => {
    if (
      financialReportingForm.multiAddCert &&
      !dynamicCriteriaSearch &&
      appState.ui.searchPush &&
      updateUi
    ) {
      updateUi("searchPush", false);
    }
  }, [
    appState.ui.searchPush,
    financialReportingForm.multiAddCert,
    dynamicCriteriaSearch,
    updateUi,
  ]);

  return (
    <Root>
      <SearchForm
        searchButtonText={
          financialReportingForm.savedPGCSearch &&
          !notSignedInWhileViewingSavedPG
            ? "Load Peer Groups"
            : undefined
        }
        header={
          noHeader ? undefined : isPeerGroup ? "" : "Search For Institutions"
        }
        noChangeSubmit={initialSubmit}
        prevForm={prevForm}
        setPrevForm={setPrevForm}
        canHideSearchPanel={false}
        disabled={disabled}
        formList={FinancialReportingList}
        form={{
          ...financialReportingForm,
        }}
        hideSearchButton={
          financialReportingForm.multiAddCert && !dynamicCriteriaSearch
        }
        login={
          financialReportingForm.savedPGCSearch &&
          notSignedInWhileViewingSavedPG
        }
        optionalIndicator={
          pgcApp === "Customized Comparisons" &&
          !financialReportingForm.savedPGCSearch
        }
        displayReset={displayReset}
        onReset={() => {
          resetForm && resetForm();
        }}
      />
    </Root>
  );
}
